import { Component, Input, Output, EventEmitter, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { DashboardService } from '../../services/dashboard.service';
import { UnifiedOnboardingService } from '../../services/unified-onboarding.service';
import { CustomClaimService } from '../../services/custom-claim.service';
import { MembershipTierService } from '../../services/membership-tier.service';
import { MembershipRole } from '../../models/team.model';
import { isNavItemLocked, billingNavLabel, billingNavRoute } from '../../utils/sidebar-tier-utils';
import { filter } from 'rxjs/operators';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  active?: boolean;
  badge?: boolean; // For notification indicators
  comingSoon?: boolean; // For features not yet available
  externalUrl?: boolean; // For external links
  noActiveHighlight?: boolean; // Prevent active styling (for shortcut links)
  children?: NavItem[]; // Sub-menu items
  expanded?: boolean; // Whether sub-menu is expanded (inline mode)
  drillDown?: boolean; // If true, uses drill-down navigation instead of inline expand
  customIcon?: string; // Path to custom SVG icon (instead of Bootstrap icon)
  /**
   * Org-scoped role gate. When set, the item only renders if the user
   * has at least one of these `MembershipRole`s in the active org.
   * Used by the Work Orders entry (Owner / Chief Supervisor only).
   */
  requireOrgRoles?: MembershipRole[];
  /**
   * Pro-tier gate. When set, a Core-tier org still sees the item (not
   * hidden — per spec.md FR-3.3) but rendered with a lock affordance;
   * clicking it navigates normally and `MembershipTierGuard` on the target
   * route redirects to that route's preview.
   */
  requireTier?: 'pro';
  /**
   * Marks the Billing entry, whose label + route switch by tier (Upgrade
   * to Pro for Core, Manage Subscription for Pro) rather than being fixed
   * like every other NavItem. See `billingLabel()`/`billingRoute()`.
   */
  dynamicBilling?: boolean;
}

interface NavSection {
  items: NavItem[];
  showDivider?: boolean; // Show divider after this section
}

/**
 * Sidebar Navigation Component
 * Left-hand navigation with ADN logo and menu items
 * Based on dashboard mockups showing dark sidebar with blue accent
 *
 * Reference: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {
  private dashboardService = inject(DashboardService);
  private unifiedOnboardingService = inject(UnifiedOnboardingService);
  private customClaimService = inject(CustomClaimService);
  private membershipTierService = inject(MembershipTierService);

  @Input() collapsed: boolean = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  // Drill-down navigation state
  activeSubmenu: NavItem | null = null;

  // Computed signal for unseen activity badge (red dot)
  readonly hasUnseenActivity = computed(() => this.dashboardService.hasUnseenActivities$());

  // Track current route for compact onboarding visibility
  private currentRoute = signal<string>('/dashboard');

  /**
   * Whether to show the compact onboarding widget in the sidebar.
   * Show when: (dismissed OR not on dashboard) AND NOT fully complete AND has loaded data
   */
  readonly showCompactOnboarding = computed(() => {
    // Don't show if no progress data loaded yet
    if (!this.unifiedOnboardingService.hasLoadedOnce()) return false;

    // Don't show if onboarding is fully complete
    if (this.unifiedOnboardingService.isFullyComplete()) return false;

    const isDismissed = this.unifiedOnboardingService.isDismissed();
    const route = this.currentRoute();
    const isOnDashboard = route === '/dashboard' || route.startsWith('/dashboard?');

    // If on dashboard: show compact only if dismissed
    if (isOnDashboard) {
      return isDismissed;
    }

    // If NOT on dashboard: always show compact version
    return true;
  });

  /**
   * Combined onboarding percentage from the unified service.
   */
  readonly combinedPercentage = computed(() => this.unifiedOnboardingService.combinedPercentage());

  // Navigation organized in sections matching mockup
  navSections: NavSection[] = [
    {
      items: [
        { label: 'Dashboard', icon: 'bi-house-door', route: '/dashboard' },
        { label: 'Drones', icon: '', route: '/drones', customIcon: 'assets/images/drone-outline.svg' },
        { label: 'Certifications', icon: 'bi-award', route: '/certifications' },
        { label: 'Customers', icon: 'bi-people', route: '/customers' },
        // Org-admin only — gated to Owner / Chief Supervisor. Pilots
        // see WOs via their mobile app's pilot-side surface, not this
        // queue view.
        {
          label: 'Work Orders',
          icon: 'bi-clipboard-check',
          route: '/work-orders',
          requireOrgRoles: [MembershipRole.Owner, MembershipRole.ChiefSupervisor],
        }
      ],
      showDivider: true
    },
    {
      items: [
        {
          label: 'CropFlight',
          icon: 'bi-journal-text',
          route: '/logbook',
          drillDown: true, // Uses drill-down navigation (replaces sidebar)
          children: [
            { label: 'Overview', icon: 'bi-grid', route: '/logbook' },
            { label: 'Spray Records', icon: 'bi-droplet-half', route: '/logbook/spray-records' },
            { label: 'Fields', icon: 'bi-geo-alt', route: '/logbook/fields' },
            {
              label: 'Chemicals',
              icon: 'bi-flask',
              route: '/logbook/chemicals',
              children: [
                { label: 'Chemical Book', icon: '', route: '/logbook/chemicals/book' },
                { label: 'Custom Chemicals / Foliar', icon: '', route: '/logbook/chemicals/custom' },
                { label: 'Chemical Templates', icon: '', route: '/logbook/chemicals/templates' }
              ]
            },
            {
              label: 'Testing',
              icon: 'bi-clipboard-check',
              route: '/logbook/testing',
              children: [
                { label: 'Safety Tests', icon: '', route: '/logbook/testing/safety' },
                { label: 'Team Report', icon: '', route: '/logbook/testing/team' },
                { label: 'Field Tests', icon: '', route: '/logbook/testing/field' }
              ]
            },
            {
              label: 'Reports',
              icon: 'bi-file-earmark-text',
              route: '/logbook/reports',
              children: [
                { label: 'FAA Reports', icon: '', route: '/logbook/reports/faa' },
                { label: 'State Reports', icon: '', route: '/logbook/reports/state' }
              ]
            }
          ]
        },
        {
          label: 'Maintenance',
          icon: 'bi-wrench',
          route: '',
          drillDown: true, // Uses drill-down navigation (replaces sidebar)
          children: [
            { label: 'Overview', icon: 'bi-grid', route: '/maintenance' },
            { label: 'Support Tickets', icon: 'bi-ticket-perforated', route: '/maintenance/support-tickets', requireTier: 'pro' },
            { label: 'Service Appointments', icon: 'bi-calendar-check', route: '/maintenance/service-appointments', requireTier: 'pro' },
            { label: 'Scheduled Maintenance', icon: 'bi-calendar-event', route: '/maintenance/scheduled', requireTier: 'pro' },
            { label: 'Training Videos', icon: 'bi-play-btn', route: '/maintenance/training-videos', requireTier: 'pro' }
          ]
        },
        // Store hidden until it's ready — restore this nav item when the
        // storefront launches.
        // { label: 'Store', icon: 'bi-cart3', route: 'https://store.americandronenetwork.com', externalUrl: true },
        { label: 'Agremo', icon: 'bi-graph-up', route: '/analyses', requireTier: 'pro' },
        { label: 'Acre Advantage', icon: 'bi-lightbulb', route: '/acres-advantage' }
      ],
      showDivider: true
    },
    {
      items: [
        { label: 'Getting Started', icon: 'bi-info-circle', route: '/getting-started', comingSoon: true },
        {
          label: 'Resources',
          icon: 'bi-play-circle',
          route: '',
          drillDown: true,
          children: [
            { label: 'Insurance', icon: 'bi-shield-check', route: 'https://agsprayinsurance.com/adn-member-coverage', externalUrl: true },
            { label: 'UPass', icon: 'bi-patch-check', route: 'https://www.upass.foundation/', externalUrl: true },
            { label: 'FieldWatch', icon: 'bi-binoculars', route: 'https://fieldwatch.com/', externalUrl: true }
          ]
        },
        { label: 'Support', icon: 'bi-life-preserver', route: '/maintenance', noActiveHighlight: true },
        // Placeholder entry (ADN-2280/2281, TG7) — Task Groups 8/9 build the
        // real /billing/upgrade + /billing pages and the tier-conditional
        // label/route swap; `comingSoon` keeps this a visible-but-inert
        // placeholder (matches the existing `Getting Started` pattern above)
        // rather than a route that 404s in the interim.
        { label: 'Billing', icon: 'bi-credit-card', route: '/billing', dynamicBilling: true }
      ],
      showDivider: false
    }
  ];

  constructor(private router: Router) {
    this.updateActiveRoute();

    // Subscribe to route changes to update active states and track current route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveRoute();
      this.currentRoute.set(this.router.url);
    });

    // Set initial route
    this.currentRoute.set(this.router.url);
  }

  /**
   * Restore onboarding card and navigate to dashboard.
   * Uses the unified service's restore() method.
   */
  restoreOnboarding(): void {
    this.unifiedOnboardingService.restore();
  }

  /**
   * Update active state based on current route
   */
  private updateActiveRoute(): void {
    const currentRoute = this.router.url.split('?')[0]; // Remove query params
    let shouldShowSubmenu: NavItem | null = null;

    this.navSections.forEach(section => {
      section.items.forEach(item => {
        // Check if current route matches this item exactly or is a child route
        item.active = item.noActiveHighlight ? false : this.isRouteActive(currentRoute, item.route);

        // Check children if they exist
        if (item.children) {
          let childActive = false;
          item.children.forEach(child => {
            // Check if this child route is active
            // Use exact matching for leaf nodes (no children), prefix matching for parents
            const isLeafNode = !child.children || child.children.length === 0;
            child.active = this.isRouteActive(currentRoute, child.route, isLeafNode);

            // Check grandchildren (third level)
            if (child.children) {
              let grandchildActive = false;
              child.children.forEach(grandchild => {
                // Grandchildren are always leaf nodes, use exact matching
                grandchild.active = this.isRouteActive(currentRoute, grandchild.route, true);
                if (grandchild.active) {
                  grandchildActive = true;
                }
              });
              // Auto-expand child if grandchild is active
              if (grandchildActive) {
                child.expanded = true;
                childActive = true;
              }
            }

            if (child.active) {
              childActive = true;
            }
          });

          // If any child is active, mark parent as active too (for main menu highlighting)
          if (childActive) {
            item.active = true;
          }

          // Determine if this drill-down submenu should be shown
          // Show submenu if a child route is active, or if the submenu was already open
          // and we're on the parent route (user clicked the parent link from within submenu)
          if (item.drillDown && (childActive || (item.active && this.activeSubmenu === item))) {
            shouldShowSubmenu = item;
          } else if (childActive && !item.drillDown) {
            // For inline expand, set expanded
            item.expanded = true;
          }
        }
      });
    });

    // Update activeSubmenu based on whether we're in a drill-down section
    this.activeSubmenu = shouldShowSubmenu;
  }

  /**
   * Check if a route is active (handles exact match and child routes)
   * @param currentRoute - The current browser route
   * @param itemRoute - The nav item's route
   * @param exactOnly - If true, only exact matches count (for leaf nodes without children)
   */
  private isRouteActive(currentRoute: string, itemRoute: string, exactOnly: boolean = false): boolean {
    // Empty routes should never be considered active
    if (!itemRoute) {
      return false;
    }
    // Exact match
    if (currentRoute === itemRoute) {
      return true;
    }
    // Child route match (current route starts with item route followed by /)
    // Only check this if not in exactOnly mode
    if (!exactOnly && currentRoute.startsWith(itemRoute + '/')) {
      return true;
    }
    return false;
  }

  /**
   * Toggle expand/collapse state for items with children
   */
  toggleExpand(item: NavItem, event: Event): void {
    event.stopPropagation();
    item.expanded = !item.expanded;
  }

  /**
   * Check if an item has children
   */
  hasChildren(item: NavItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  /**
   * Visibility gate for an item. Currently only honors the org-scoped
   * `requireOrgRoles` field — items without it are always visible.
   * Falls back to the original behavior so existing nav stays intact.
   */
  isVisible(item: NavItem): boolean {
    if (!item.requireOrgRoles || item.requireOrgRoles.length === 0) return true;
    return this.customClaimService.hasAnyOrgRole(item.requireOrgRoles);
  }

  /**
   * Lock affordance for Pro-only items on a Core-tier org. The item stays
   * visible and clickable either way (per spec.md FR-3.3) — this only
   * drives the visual lock icon/class; the actual gate is
   * `MembershipTierGuard` on the target route.
   *
   * Gated on `hasFetched()` — `AuthService` fires `fetchStatus()` without
   * awaiting it, so right after login `isPro()` can still hold its
   * pre-fetch Core default. Without this guard, a Pro org would flash a
   * lock icon on every Pro-only item until the fetch resolves. Showing no
   * lock during that brief unknown-tier window is the safer default than
   * showing a wrong one.
   */
  isLocked(item: NavItem): boolean {
    return isNavItemLocked(item, this.membershipTierService.hasFetched(), this.membershipTierService.isPro());
  }

  /**
   * Label/route for the Billing nav item (ADN-2282/2283, TG8/TG9) — spec.md
   * FR-3.3/UI Requirements: "Billing entry switches label/route by tier."
   * Before the tier fetch resolves, defaults to the Core-side (Upgrade to
   * Pro) rather than showing nothing — a brief incorrect label on a
   * clickable-either-way nav item is a much smaller cost than the lock-icon
   * flicker `isLocked()` guards against (that one could misrepresent a
   * paying org as blocked; this one just risks one stale click before the
   * fetch resolves, and the destination page's own ngOnInit re-checks tier
   * and redirects correctly regardless).
   */
  billingLabel(): string {
    return billingNavLabel(this.membershipTierService.isPro());
  }

  billingRoute(): string {
    return billingNavRoute(this.membershipTierService.isPro());
  }

  /**
   * Check if the submenu header (parent item) route is the active route
   */
  isSubmenuHeaderActive(): boolean {
    if (!this.activeSubmenu?.route) return false;
    const currentRoute = this.router.url.split('?')[0];
    return currentRoute === this.activeSubmenu.route;
  }

  /**
   * Navigate to the submenu parent's route
   */
  navigateToSubmenuParent(): void {
    if (this.activeSubmenu?.route) {
      this.router.navigate([this.activeSubmenu.route]);
    }
  }

  /**
   * Navigate to route (only if not coming soon)
   */
  navigateTo(item: NavItem, event?: Event): void {
    // If item has children, handle based on drillDown setting
    if (this.hasChildren(item)) {
      if (item.drillDown) {
        // Drill-down mode: replace sidebar content and navigate to parent route
        this.activeSubmenu = item;
        if (item.route && !item.externalUrl) {
          this.router.navigate([item.route]);
        } else if (!item.route && item.children && item.children.length > 0) {
          // If parent has no route, navigate to first child's route
          const firstChild = item.children.find(child => !child.comingSoon && !child.externalUrl && child.route);
          if (firstChild) {
            this.router.navigate([firstChild.route]);
          }
        }
      } else {
        // Inline expand mode: toggle expand
        if (event) {
          this.toggleExpand(item, event);
        }
      }
      return;
    }

    if (item.comingSoon) {
      return; // Don't navigate to coming soon features
    }

    if (item.externalUrl) {
      window.open(item.route, '_blank');
      return;
    }

    this.router.navigate([item.dynamicBilling ? this.billingRoute() : item.route]);
    // Note: updateActiveRoute() is called automatically via the router events subscription

    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      this.onToggleSidebar();
    }
  }

  /**
   * Go back from drill-down submenu to main nav
   */
  goBackFromSubmenu(): void {
    this.activeSubmenu = null;
  }

  /**
   * Emit toggle sidebar event
   */
  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  /**
   * Close sidebar (for mobile close button)
   */
  closeSidebar(): void {
    this.onToggleSidebar();
  }
}
